import {
  ConnectionErrorCode,
  ConnectionStatus,
} from "../../../api/integrations";

type StatusMeta = { dot: string; label: string; text: string; pulse?: boolean };

// Status semantics from the connection state machine (SKE-3948):
// green = active, blue = syncing (live), amber = needs config, red = error,
// grey = paused/disconnected.
const STATUS_META: Record<ConnectionStatus, StatusMeta> = {
  // The settled states let the dot carry the colour and keep the label in primary
  // text (SKE-4124) — a green dot beside dark "Active" reads calmer than green-on-
  // green, and error/paused stay coloured so they still stand out against it.
  active: { dot: "bg-green-500", label: "Active", text: "text-neutral-800" },
  // Live transient state (SKE-3941): a save just started the first sync. A spinning
  // glyph replaces the dot so it reads as in-motion, not settled — gated on
  // motion-safe, which leaves a reduced-motion user a static spinner, never animation.
  syncing: {
    dot: "bg-blue-500",
    label: "Syncing…",
    text: "text-neutral-800",
    pulse: true,
  },
  pending_config: {
    dot: "bg-amber-500",
    label: "Needs setup",
    text: "text-amber-700",
  },
  error: { dot: "bg-red-500", label: "Error", text: "text-red-700" },
  paused: { dot: "bg-neutral-400", label: "Paused", text: "text-neutral-600" },
  disconnected: {
    dot: "bg-neutral-300",
    label: "Disconnected",
    text: "text-neutral-500",
  },
};

// A `sync_failing` connection is `error` on the wire, but its access is fine and
// we keep retrying — so it must read as AMBER "Syncing issues", never a red
// "Error" that would contradict the amber banner below it (SKE-3955). Real
// credential errors stay red.
const SYNC_FAILING_META: StatusMeta = {
  dot: "bg-amber-500",
  label: "Syncing issues",
  text: "text-amber-700",
};

function badgeMeta(status: ConnectionStatus, errorCode?: ConnectionErrorCode) {
  if (status === "error" && errorCode === "sync_failing") {
    return SYNC_FAILING_META;
  }
  return STATUS_META[status] ?? STATUS_META.disconnected;
}

export function StatusDot({
  status,
  errorCode,
}: {
  status: ConnectionStatus;
  errorCode?: ConnectionErrorCode;
}) {
  const meta = badgeMeta(status, errorCode);
  // A live sync shows a spinning glyph rather than a dot, so "in flight" reads
  // differently in kind from the settled states — not just in colour. The spin is
  // motion-safe only, leaving a reduced-motion user a static spinner.
  if (meta.pulse) {
    return (
      <span
        className="inline-flex size-5 shrink-0 items-center justify-center"
        aria-hidden="true"
      >
        <i className="fa-regular fa-spinner-third text-base text-blue-500 motion-safe:animate-spin" />
      </span>
    );
  }
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dot}`}
      aria-hidden="true"
    />
  );
}

export function StatusBadge({
  status,
  errorCode,
}: {
  status: ConnectionStatus;
  errorCode?: ConnectionErrorCode;
}) {
  const meta = badgeMeta(status, errorCode);
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm ${meta.text}`}>
      <StatusDot status={status} errorCode={errorCode} />
      {meta.label}
    </span>
  );
}

const SURFACE_LABEL: Record<string, string> = {
  ideas: "Ideas",
  library: "Library",
  inbox: "Inbox",
};

// Each supported surface carries its own glyph in the design (SKE-4124), matching the
// icon the surface uses elsewhere in the product. An unknown surface still renders its
// raw key as a label, just without an icon, so a new surface can never break the header.
const SURFACE_ICON: Record<string, string> = {
  ideas: "fa-lightbulb",
  library: "fa-books",
  inbox: "fa-envelope",
};

// The "supporting platforms" chip: a raised pill rather than a blue fill, so a row of
// them reads as capability metadata next to the provider name instead of competing with
// the status badge beside it. `raised` (white) sits on the page beside the provider
// name; `tint` (blue/25) sits on a white config card, where white-on-white would
// disappear (SKE-4124).
export function SurfaceBadge({
  surface,
  tone = "raised",
}: {
  surface: string;
  tone?: "raised" | "tint";
}) {
  const icon = SURFACE_ICON[surface];
  return (
    <span
      className={`inline-flex h-5 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-500 shadow-sm ${
        tone === "tint" ? "bg-blue-25" : "bg-white"
      }`}
    >
      {icon && (
        <span
          className="inline-flex size-[15px] shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          <i className={`fa-regular ${icon} text-xs`} />
        </span>
      )}
      {SURFACE_LABEL[surface] ?? surface}
    </span>
  );
}
